import { ChevronRight, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/ui/link";
import { Skeleton } from "@/components/ui/skeleton";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./widget-card.i18n";

type WidgetCardProps = {
  icon: LucideIcon;
  title: string;
  description?: string;
  /** A count or short label beside the title ("5 clients", "Latest 5"). */
  badge?: ReactNode;
  /** Controls in the top-right corner — the period switch. */
  actions?: ReactNode;
  /** A link under the content ("Open clients review"). */
  footer?: { label: string; to: string };
  isLoading?: boolean;
  error?: unknown;
  children?: ReactNode;
  className?: string;
};

/** The shell every section widget shares: header, body, optional footer link. */
export function WidgetCard({
  icon: Icon,
  title,
  description,
  badge,
  actions,
  footer,
  isLoading,
  error,
  children,
  className,
}: WidgetCardProps) {
  const t = useT(T);

  return (
    <Card className={cn("flex h-full flex-col gap-4 p-6", className)}>
      {/* Wraps on a narrow screen, so the period switch drops below the title. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-48 flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 shrink-0" />
            <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
          </div>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
        {(actions || badge !== undefined) && (
          <div className="flex shrink-0 items-center gap-2">
            {actions}
            {badge !== undefined && <Badge variant="secondary">{badge}</Badge>}
          </div>
        )}
      </div>

      {error ? (
        <p className="text-sm text-muted-foreground">{t.widgetFailed}</p>
      ) : isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-2/3" />
        </div>
      ) : (
        children
      )}

      {footer && !error && !isLoading && (
        <Link
          to={footer.to}
          className="mt-auto inline-flex items-center gap-1 text-sm font-medium hover:underline"
        >
          {footer.label}
          <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      )}
    </Card>
  );
}
